import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, CalendarClock, Package } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import UpcomingDueCustomers from './UpcomingDueCustomers';
import InstallmentSummaryCards from './InstallmentSummaryCards';
import GreetingHeader from './GreetingHeader';
import TargetProgress from '../common/TargetProgress';
import UserBadge from '../common/UserBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SalesExecutiveDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/sales-executive-stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => setStats({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400">Loading dashboard...</div>;

  const cards = [
    { title: 'My Customers', value: stats?.my_customers || 0, icon: Users, color: 'text-blue-500', testId: 'sales-stat-customers' },
    { title: 'My Customers This Month', value: stats?.my_customers_month || 0, icon: Users, color: 'text-emerald-500', testId: 'sales-stat-customers-month' },
    { title: 'My Installments', value: stats?.my_installments || 0, icon: CalendarClock, color: 'text-amber-500', testId: 'sales-stat-installments' },
    { title: 'My Installments This Month', value: stats?.my_installments_month || 0, icon: CalendarClock, color: 'text-emerald-500', testId: 'sales-stat-installments-month' },
    { title: 'Inventory Available', value: stats?.inventory_available || 0, icon: Package, color: 'text-purple-500', testId: 'sales-stat-inventory' },
  ];

  return (
    <div className="space-y-6" data-testid="sales-executive-dashboard">
      <GreetingHeader
        testId="sales-greeting"
        fallbackName="Sales Executive"
        subtitle="Sales Executive Dashboard · Track your onboarded customers and closed installment deals"
        extra={<UserBadge badge={stats?.badge} testId="sales-user-badge" />}
      />
      <TargetProgress
        target={stats?.monthly_target || 0}
        current={stats?.my_customers_month || 0}
        testId="sales-target-progress"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={c.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">{c.title}</CardTitle>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold text-white">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>
      <Card className="bg-slate-900 border-slate-800" data-testid="sales-chart">
        <CardHeader><CardTitle className="text-white text-base">My Pipeline</CardTitle></CardHeader>
        <CardContent style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[
              { name: 'Customers', all: stats?.my_customers || 0, month: stats?.my_customers_month || 0 },
              { name: 'Installments', all: stats?.my_installments || 0, month: stats?.my_installments_month || 0 },
              { name: 'Inventory', all: stats?.inventory_available || 0, month: 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#fff' }} />
              <Bar dataKey="all" name="Total" fill="#F59E0B" radius={[4,4,0,0]} />
              <Bar dataKey="month" name="This Month" fill="#10B981" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    <InstallmentSummaryCards />
      <UpcomingDueCustomers />

    </div>
  );
}
